import { Box, BoxProps, Link, LinkProps } from '@mui/material';
import { default as MarkdownToJSXComponent, MarkdownToJSX } from 'markdown-to-jsx';
import { useMemo } from 'react';
import { SizingMultiplier } from '../sizing-multiplier.constant';
import { Typography, TypographyProps } from '../typography/typography.component';

export type MarkdownProps = TypographyProps & {
  aProps?: LinkProps;
  pProps?: TypographyProps;
  ulProps?: BoxProps<'ul'>;
  olProps?: BoxProps<'ol'>;
  liProps?: TypographyProps;
  children: string;
};

const getMarkdownOptions = ({
  aProps,
  pProps,
  ulProps,
  olProps,
  liProps,
  ...wrapperProps
}: Omit<MarkdownProps, 'children'>): MarkdownToJSX.Options => ({
  wrapper: Typography,
  wrapperProps: {
    component: 'article',
    color: 'inherit',
    ...wrapperProps,
  } satisfies TypographyProps,
  forceWrapper: true,
  forceBlock: true,
  overrides: {
    a: {
      component: Link,
      props: {
        color: 'inherit',
        fontSize: 'inherit',
        underline: 'always',
        target: '_blank',
        rel: 'noreferrer',
        ...aProps,
      } satisfies MarkdownProps['aProps'],
    },
    p: {
      component: Typography,
      props: {
        color: 'inherit',
        fontSize: 'inherit',
        mb: SizingMultiplier.xs,
        ...pProps,
      } satisfies MarkdownProps['pProps'],
    },
    ul: {
      component: Box,
      props: {
        component: 'ul',
        color: 'inherit',
        fontSize: 'inherit',
        pl: SizingMultiplier.md,
        mt: 0,
        mb: SizingMultiplier.xs,
        ...ulProps,
      } satisfies MarkdownProps['ulProps'],
    },
    ol: {
      component: Box,
      props: {
        component: 'ol',
        color: 'inherit',
        fontSize: 'inherit',
        pl: SizingMultiplier.md,
        mt: 0,
        mb: SizingMultiplier.xs,
        ...olProps,
      } satisfies MarkdownProps['olProps'],
    },
    li: {
      component: Typography,
      props: {
        component: 'li',
        color: 'inherit',
        fontSize: 'inherit',
        ...liProps,
      } satisfies MarkdownProps['liProps'],
    },
  },
});

export const Markdown = ({ children, aProps, pProps, ulProps, olProps, liProps, ...wrapperProps }: MarkdownProps) => {
  const markdownOptions = getMarkdownOptions({ aProps, pProps, ulProps, olProps, liProps, ...wrapperProps });
  const sanitizedChildren = useMemo(() => children.replace(/\\n/g, '\n'), [children]);
  return <MarkdownToJSXComponent options={markdownOptions}>{sanitizedChildren}</MarkdownToJSXComponent>;
};
